import type { Scene, SceneElement, TextElement } from './types.js';

/**
 * Serbian script fallbacks: when a text layer's font cannot write what the
 * shopper typed, the whole layer switches to the font's "twin" that can.
 *
 * Serbian is written in two alphabets, and a great many shoppers type their
 * names in Cyrillic ("Ђорђе и Ана"). Most decorative fonts a shop buys or
 * imports have no Cyrillic at all — and some have no č ć đ š ž either — so the
 * print came out with empty boxes. The shop picks (or the platform picks for
 * it: services/font-fallbacks.ts) a twin for such a font: the closest face
 * that covers BOTH Serbian alphabets. The switch is per layer and all-or-
 * nothing: mixing two typefaces inside one name looks broken, a whole name in
 * the nearest matching face does not.
 *
 * This file is the one rule. The renderer applies it before it lays out a text
 * layer (raster, vector and measurement alike), so the widget preview, the
 * Studio canvas and the print file always agree on which font was used.
 */

/** Scripts a twin is chosen for. `latin-ext` means the Serbian Latin letters. */
export type FallbackScript = 'cyrillic' | 'latin-ext';

/** A font's twin, as stored on a text layer's `font.fallback`. */
export interface FontFallback {
  /** The face to switch to. */
  fontId: string;
  /** Which of the layer's own font's gaps it fills; the switch happens only for these. */
  scripts: FallbackScript[];
  /**
   * The font this twin was chosen for. A layer whose font changed after it was
   * marked (a font-choice answer) ignores a twin chosen for the old one.
   */
  of?: string;
}

/** Serbian Cyrillic, both cases — the letters a Serbian name can contain. */
export const SERBIAN_CYRILLIC =
  'АБВГДЂЕЖЗИЈКЛЉМНЊОПРСТЋУФХЦЧЏШабвгдђежзијклљмнњопрстћуфхцчџш';
/** The Serbian Latin letters beyond basic ASCII. */
export const SERBIAN_LATIN_EXTRA = 'ČĆĐŠŽčćđšž';

/** Which of the fallback scripts some text actually uses. */
export function scriptsIn(text: string): FallbackScript[] {
  const out: FallbackScript[] = [];
  // Any Cyrillic letter, Serbian or not: a Russian or Ukrainian name set in a
  // Latin-only face is the same empty box.
  if (/[Ѐ-ӿ]/u.test(text)) out.push('cyrillic');
  if (/[ČĆĐŠŽčćđšž]/u.test(text)) out.push('latin-ext');
  return out;
}

/** The text a layer will print, for the script check (the renderer's own resolution lives in scene-render). */
function layerText(el: TextElement): string {
  const value = typeof el.value === 'string' ? el.value : typeof el.default === 'string' ? el.default : '';
  return `${el.prefix ?? ''}${value}${el.suffix ?? ''}`;
}

/**
 * The layer as it should be drawn: in its twin when its text uses a script
 * its own font lacks, otherwise exactly as it is (same object).
 */
export function withScriptFallback(el: TextElement, text: string = layerText(el)): TextElement {
  const twin = twinFor(el.font.fontId, el.font.fallback, text);
  if (!twin) return el;
  return {
    ...el,
    font: { ...el.font, fontId: twin, substitutedFrom: el.font.fontId },
  };
}

/**
 * The face `text` set in `fontId` is drawn in: the font's twin (`twins` is the
 * definition's `fontTwins`) when the text uses a script the font lacks,
 * otherwise the font itself. The same switch as `withScriptFallback`, for a
 * font that is not on a layer — a font picker's sample of the shopper's words.
 */
export function drawnFontId(fontId: string, text: string, twins: Record<string, FontFallback> | undefined): string {
  return twinFor(fontId, twins?.[fontId], text) ?? fontId;
}

/** The twin to switch to, or null when `fontId` can write `text` itself. */
function twinFor(fontId: string, fallback: FontFallback | undefined, text: string): string | null {
  if (!fallback?.fontId || fallback.fontId === fontId) return null;
  if (fallback.of && fallback.of !== fontId) return null;
  const needed = scriptsIn(text);
  return needed.some((script) => fallback.scripts.includes(script)) ? fallback.fontId : null;
}

/**
 * Marks every text layer whose font has a twin, so the renderer can switch
 * when the typed text needs it. `twins` maps a font id to its twin; fonts that
 * cover both Serbian alphabets are simply absent. A layer that already carries
 * a fallback for its current font keeps it (a frozen order is never re-decided).
 */
export function annotateFontFallbacks(scene: Scene, twins: Record<string, FontFallback>): Scene {
  let changed = false;
  const mark = (el: SceneElement): SceneElement => {
    if (el.type !== 'text') return el;
    const current = el.font.fallback;
    if (current && (!current.of || current.of === el.font.fontId)) return el;
    const twin = twins[el.font.fontId];
    if (!twin) return el;
    changed = true;
    return { ...el, font: { ...el.font, fallback: { ...twin, of: el.font.fontId } } };
  };
  const elements = scene.elements.map(mark);
  return changed ? { ...scene, elements } : scene;
}

// ── Choosing a twin ────────────────────────────────────────────────────────

export type FontCategory =
  | 'serif' | 'sans' | 'script' | 'handwriting' | 'display' | 'decorative' | 'mono' | 'symbols';

const CATEGORIES: FontCategory[] = ['serif', 'sans', 'script', 'handwriting', 'display', 'decorative', 'mono', 'symbols'];

/** Tested in order; the same patterns the shopper's font picker groups by. */
const CATEGORY_PATTERNS: Array<[FontCategory, RegExp]> = [
  ['mono', /\bmono|monospace|code|courier|consol|typewriter/],
  ['symbols', /symbol|dingbat|emoji|icon|wingding|ornament/],
  ['script', /script|calligra|cursive|brush|signature|pacifico|dancing|vibes|allura|sacramento|lobster|comforter/],
  ['handwriting', /hand|write|written|marker|comic|caveat|indie|shadows into|patrick|kalam|schoolbell|pangolin|balsamiq|shantell|marmelad/],
  ['decorative', /decor|fancy|stencil|distress|grunge|retro|vintage|neon|western|pixel|8-?bit|graffiti|horror|sci-?fi|bubbles/],
  // "Arno Pro Display", "Playfair Display": a serif's display cut, not a poster face.
  ['serif', /serif|slab|times|georgia|garamond|playfair|merriweather|roman|bookman|baskerville|didone|antiqua|lora|alegreya|yeseva|arno|caslon|bodoni|minion|fatface/],
  ['display', /bebas|anton|oswald|league gothic|impact|condensed|russo|unbounded|ruslan|amatic|luckiest|poster|headline|\btitle\b|\bfat\b/],
  ['sans', /\bsans\b|grotesk|grotesque|helvetica|arial|\binter\b|roboto|montserrat|lato|poppins|nunito|rubik|raleway|ubuntu|round|comfortaa|andika|exo|fredoka|baloo/],
  ['display', /display/],
];

/**
 * Faces that make a good, neutral twin for their kind: when nothing closer
 * decides it, a name in an unknown sans prints in Montserrat rather than in
 * whichever face sorts first alphabetically.
 */
const NEUTRAL_TWINS: Record<FontCategory, string[]> = {
  sans: ['montserrat', 'raleway', 'rubik', 'nunito', 'exo 2'],
  serif: ['lora', 'playfair display', 'merriweather', 'alegreya'],
  script: ['great vibes', 'marck script', 'bad script', 'lobster', 'pacifico'],
  handwriting: ['caveat', 'pangolin', 'shantell sans', 'bad script'],
  display: ['oswald', 'russo one', 'unbounded', 'yeseva one'],
  decorative: ['comfortaa', 'rubik bubbles', 'amatic sc'],
  mono: ['montserrat'],
  symbols: ['montserrat'],
};

/** A face's kind: the shop's own `category` when it set one, else a guess from the name. */
export function fontCategory(font: { family: string; style?: string | null; category?: string | null }): FontCategory {
  if (font.category && CATEGORIES.includes(font.category as FontCategory)) return font.category as FontCategory;
  const name = `${font.family} ${font.style ?? ''}`.toLowerCase();
  for (const [category, pattern] of CATEGORY_PATTERNS) if (pattern.test(name)) return category;
  return 'sans';
}

/** Categories that read alike, so a script face without a script twin gets handwriting, not a grotesque. */
const NEAR: Record<FontCategory, FontCategory[]> = {
  script: ['handwriting', 'decorative', 'display'],
  handwriting: ['script', 'decorative', 'sans'],
  decorative: ['display', 'handwriting', 'script'],
  display: ['decorative', 'sans', 'serif'],
  serif: ['display', 'sans'],
  sans: ['display', 'serif'],
  mono: ['sans'],
  symbols: ['sans'],
};

export interface TwinCandidate {
  id: string;
  family: string;
  style?: string | null;
  category?: string | null;
  /** Covers Serbian Cyrillic AND Serbian Latin. */
  serbian: boolean;
  /** Not yet in the shop's library (a bundled face that would be installed). */
  bundled?: boolean;
}

/**
 * The closest face that writes both Serbian alphabets.
 *
 * In order: the same family (many families ship a Cyrillic cut under the same
 * name), then the same kind of face, then a related kind; within those the
 * same weight and slant, and a face already in the shop's library over one
 * that would have to be installed. Undefined when nothing covers Serbian.
 */
export function chooseTwin(
  font: { family: string; style?: string | null; category?: string | null },
  candidates: readonly TwinCandidate[],
): TwinCandidate | undefined {
  const category = fontCategory(font);
  const bold = /bold|black|heavy|semibold/i.test(font.style ?? '');
  const italic = /italic|oblique/i.test(font.style ?? '');
  const baseName = (family: string): string => family.toLowerCase().replace(/\b(pro|std|display|text|cyrillic|cyr)\b/g, '').replace(/\s+/g, ' ').trim();
  let best: { twin: TwinCandidate; score: number } | undefined;
  for (const twin of candidates) {
    if (!twin.serbian) continue;
    let score = 0;
    const sameFamily = baseName(twin.family) === baseName(font.family);
    if (sameFamily) score += 100;
    const kind = fontCategory(twin);
    if (kind === category) score += 40;
    else {
      const near = NEAR[category].indexOf(kind);
      if (near >= 0) score += 20 - near * 5;
    }
    if (/bold|black|heavy|semibold/i.test(twin.style ?? '') === bold) score += 6;
    if (/italic|oblique/i.test(twin.style ?? '') === italic) score += 4;
    // A known good neutral for this kind beats an arbitrary face of it; earlier
    // in the list is better.
    // (Within the same family, weight and slant decide instead.)
    const neutral = NEUTRAL_TWINS[category].indexOf(twin.family.toLowerCase());
    if (neutral >= 0 && !sameFamily) score += 10 - neutral;
    if (!twin.bundled) score += 3;
    if (!best || score > best.score || (score === best.score && twin.family < best.twin.family)) {
      best = { twin, score };
    }
  }
  return best?.twin;
}
